import React, { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, Type } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DesignFields } from "@/components/DesignFields";
import { PaletteVariants } from "@/components/PaletteVariants";
import { WordmarkFontPicker } from "@/components/WordmarkFontPicker";
import { toast } from "sonner";

/** تعديل بيانات قطعة تصميم في أي وقت من المكتبة (الأسماء، الأرقام، الأصناف والأسعار، رابط QR) وإعادة تركيبها فورًا — بلا رصيد */
export const RecomposeDialog = ({ item, onClose, onSaved, onAdvanced }) => {
  const [inputs, setInputs] = useState({});
  const [brand, setBrand] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [abstractBg, setAbstractBg] = useState(false);
  const [full, setFull] = useState(null);
  useEffect(() => {
    if (!item) return;
    api.get(`/content/${item.id}`).then((r) => {
      setFull(r.data);
      setInputs(r.data.design_inputs || {});
      setBrand(r.data.ov?.brand_name || "");
      setInfo(r.data.ov?.info || "");
    }).catch(() => {});
  }, [item]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/content/${item.id}/recompose`, { design_inputs: inputs, brand_name: brand, overlay_text: info, abstract_bg: abstractBg });
      toast.success(data.has_back ? "تم تحديث البطاقة بوجهيها — رمز QR يحفظ جهة الاتصال (vCard)" : "تم تحديث التصميم ببياناتك الجديدة");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const isCard = item?.item_kind === "business_card";
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recompose-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-primary" /> تعديل «{item?.title}»</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">عدّل أي بيان{isCard && " أو بدّل صورتك الشخصية"} ثم اضغط حفظ — يُعاد تركيب التصميم فورًا على نفس الخلفية المولَّدة، بلا رصيد ولا توليد جديد، وبلا حدود. {isCard && "رمز QR في البطاقة يحفظ جهة الاتصال كاملة (vCard)، والوجه الخلفي يحمل شعارك."}</p>
        {full?.data_back && (
          <div className="grid grid-cols-2 gap-2" data-testid="recompose-faces">
            <div><Label className="text-[11px] mb-1 block">الوجه الأمامي</Label><img src={`data:image/png;base64,${full.data}`} alt="" className="rounded-lg border border-border w-full" /></div>
            <div><Label className="text-[11px] mb-1 block">الوجه الخلفي (الشعار)</Label><img src={`data:image/png;base64,${full.data_back}`} alt="" className="rounded-lg border border-border w-full" /></div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs">الاسم التجاري / الشركة</Label><Input value={brand} onChange={(e) => setBrand(e.target.value)} className="bg-secondary border-border" data-testid="recompose-brand" /></div>
          <div><Label className="mb-1 block text-xs">معلومات إضافية (عرض، فرع…)</Label><Input value={info} onChange={(e) => setInfo(e.target.value)} className="bg-secondary border-border" data-testid="recompose-info" /></div>
        </div>
        {item && <DesignFields kind={item.item_kind} value={inputs} onChange={setInputs} />}
        {item?.item_kind === "logo" && <WordmarkFontPicker value={inputs.wordmark_font || "bold"} brand={inputs.brand_name || full?.ov?.brand_name || ""} onChange={(k) => setInputs((d) => ({ ...d, wordmark_font: k }))} />}
        {item?.item_kind === "logo" && (
          <div className="space-y-1" data-testid="recompose-logo-palette">
            <Label className="text-xs">ألوان اللوقو — غيّر اللوحة ويُعاد تلوين الرمز والاسم فورًا بلا توليد جديد</Label>
            <PaletteVariants base={full?.brand_guide?.palette || (inputs.concept_palette ? inputs.concept_palette.split(",").map((s) => s.trim()) : [])}
              selected={inputs.concept_palette ? inputs.concept_palette.split(",").map((s) => s.trim()) : (full?.brand_guide?.palette || [])}
              onPick={(cols) => setInputs((d) => ({ ...d, concept_palette: cols.join(", ") }))} testid="recompose-palette-variants" />
          </div>
        )}
        {isCard && (
          <div className="space-y-1" data-testid="recompose-card-palette">
            <Label className="text-xs">ألوان البطاقة — اختر اللوحة وتُعاد صياغة الخلفية والعناصر بألوانك عند الحفظ</Label>
            <PaletteVariants base={inputs._palette ? inputs._palette.split(",").map((s) => s.trim()) : (full?.brand_guide?.palette || [])}
              selected={inputs._palette ? inputs._palette.split(",").map((s) => s.trim()) : (full?.brand_guide?.palette || [])}
              onPick={(cols) => setInputs((d) => ({ ...d, _palette: cols.join(", ") }))} testid="recompose-card-palette-variants" />
          </div>
        )}
        {isCard && (
          <label className="flex items-start gap-2 text-xs cursor-pointer rounded-lg border border-border px-3 py-2" data-testid="recompose-abstract-bg">
            <input type="checkbox" checked={abstractBg} onChange={(e) => setAbstractBg(e.target.checked)} className="mt-0.5 accent-primary" />
            <span>استبدل الخلفية المولَّدة بخلفية تجريدية نظيفة بألوان علامتك <span className="text-muted-foreground">— إن ظهر في الفن شخص أو عنصر لا يليق. بلا رصيد.</span></span>
          </label>
        )}
        <Button onClick={save} disabled={busy} className="w-full gap-2" data-testid="recompose-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ وإعادة التركيب — بلا رصيد</Button>
        {onAdvanced && full && (
          <Button type="button" variant="outline" className="w-full gap-2 border-border" onClick={() => onAdvanced(full)} data-testid="recompose-advanced"><Type className="w-4 h-4" /> تحرير يدوي: النصوص والشعار وتنزيل PNG</Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
